import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular/lazy';
import { BehaviorSubject } from 'rxjs';
import { ApiClientService, ClientLoginResult, CurrentUserService, LocalStorageKeys } from '@smartmoving/sm-shared-lib';
import { LoginForm } from '@generated/Authentication/login-form';
import { ThirdPartyIntegrationsService } from '@services/third-party-integration.service';
import { HttpErrorResponse } from '@angular/common/http';

export enum AuthError {
  NetworkError = 'NetworkError'
}

@Injectable({
  providedIn: 'root',
})
export class AuthProvider {
  readonly user$: BehaviorSubject<ClientLoginResult> = new BehaviorSubject(null);
  readonly authURL = 'authentication/login';

  constructor(private readonly apiClient: ApiClientService,
              private readonly currentUserService: CurrentUserService,
              private readonly navController: NavController,
              private readonly thirdPartyIntegrations: ThirdPartyIntegrationsService) {
  }

  async initialize() {
    if (localStorage.getItem(LocalStorageKeys.CURRENT_USER)) {
      const user = JSON.parse(localStorage.getItem(LocalStorageKeys.CURRENT_USER));
      await this.updateUser(user);
      this.thirdPartyIntegrations.init(user);
    }
  }

  getToken() {
    return this.currentUserService.instance?.token;
  }

  async login(loginForm: LoginForm) {
    try {
      const user = await this.apiClient.post<ClientLoginResult>(this.authURL, loginForm);
      if (!user.isLockedOutDueToPayment) {
        this.currentUserService.loginFromResult(user as ClientLoginResult, () => this.updateUser(user));
      }
      return user;
    } catch (error) {
      if (this.isNetworkIssue(error)) {
        throw AuthError.NetworkError;
      }
      throw error;
    }
  }

  async updateUser(user) {
    if (!user) {
      this.currentUserService.logout();
    }
    this.user$.next(user);
  }

  async logout() {
    await this.updateUser(null);
    this.currentUserService.logout();
    this.thirdPartyIntegrations.shutdown();
    await this.navController.navigateRoot('/login', { replaceUrl: true });
  }

  private isNetworkIssue(error): boolean {
    // navigator.onLine (the source of isOnline on web) can falsely report offline while the
    // device is online, so classify purely by the actual request outcome. A genuine offline
    // failure surfaces as status 0.
    if (error instanceof HttpErrorResponse) {
      // Gateway Timeout, Service Unavailable, Request Timeout, or 0 aka server did not return a response
      if ([504, 503, 408, 0].includes(error.status)) {
        return true;
      }
    }

    return false;
  }
}
